import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { getAuditLog, exportAuditLog } from "../../lib/api";
import { PageLoader } from "../../components/ui/spinner";
import { Button } from "../../components/ui/button";

const PAGE_SIZE = 10;

const FILTER_TABS = [
  { key: "upload", label: "Upload" },
  // FMP-AT-002 "review": the approver's sign-off of a baseline (FIRMWARE_APPROVED).
  { key: "review", label: "Review" },
  { key: "download", label: "Download" },
  { key: "ota-success", label: "OTA Success" },
  { key: "deprecate", label: "Deprecate" },
  { key: "users", label: "Users" },
];

function formatTs(ts) {
  if (!ts) return { date: "-", time: "-" };
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return { date: "-", time: "-" };
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const date = `${lookup.year}-${lookup.month}-${lookup.day}`;
  const time = `${lookup.hour}:${lookup.minute}`;
  return { date, time };
}

function getTodayDateString() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
}

export default function AuditLogPage() {
  const todayDate = getTodayDateString();
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("upload");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, totalRecords: 0, totalPages: 1 });
  const [loadError, setLoadError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState(null);

  // Only the answer to the latest request may fill the table: a slower answer to an
  // earlier tab / date choice would otherwise land last and show rows for filters
  // that are no longer selected (TS-03-TC-0003).
  const latestRequest = useRef(0);

  const fetchPage = useCallback((page = 1) => {
    const request = ++latestRequest.current;
    const isCurrent = () => request === latestRequest.current;
    setLoading(true);
    setLoadError(null);
    getAuditLog({ page, limit: PAGE_SIZE, fromDate, toDate, category: activeTab })
      .then(({ records: recs, pagination: pageInfo }) => {
        if (!isCurrent()) return;
        setRecords(recs);
        setPagination({
          page:         pageInfo.page         ?? page,
          limit:        pageInfo.limit        ?? PAGE_SIZE,
          totalRecords: pageInfo.totalRecords ?? recs.length,
          totalPages:   pageInfo.totalPages   ?? 1,
        });
      })
      .catch((err) => {
        if (!isCurrent()) return;
        setRecords([]);
        setPagination({ page: 1, limit: PAGE_SIZE, totalRecords: 0, totalPages: 1 });
        setLoadError(err.message || "Failed to load the activity trace.");
      })
      .finally(() => { if (isCurrent()) setLoading(false); });
  }, [activeTab, fromDate, toDate]);

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  // Search stays client-side; tab/date changes re-fetch server-side.
  const handleSearch = (val) => { setSearch(val); };
  const handleTab = (key) => { setActiveTab(key); };
  const handleFromDate = (val) => { setFromDate(val); };
  const handleToDate = (val) => { setToDate(val); };

  // FMP-AT-001 export: the service returns every entry for this tab and date range.
  const handleExport = () => {
    setExporting(true);
    setExportNote(null);
    exportAuditLog({ fromDate, toDate, category: activeTab })
      .then(({ fileName, rows }) => setExportNote({ ok: true, text: `Exported ${rows.toLocaleString()} entries to ${fileName}` }))
      .catch((err) => setExportNote({ ok: false, text: err.message || "Export failed" }))
      .finally(() => setExporting(false));
  };

  const filtered = records
    .filter((r) => {
      const q = search.toLowerCase();
      return !q || r.actor?.toLowerCase().includes(q) || r.fwVersion?.toLowerCase().includes(q) || r.device?.toLowerCase().includes(q) || r.eventType?.toLowerCase().includes(q);
    })
    .sort((a, b) => new Date(b.ts) - new Date(a.ts));
  const pageRecords = filtered.slice(0, PAGE_SIZE);
  const isInitialLoading = loading && records.length === 0;
  const hasFiltersApplied = search || fromDate || toDate;
  const hideSerial = ["upload", "ota-success", "users"].includes(activeTab);
  const hideDeviceBaseline = ["upload", "ota-success"].includes(activeTab);
  const hideFwVersion = ["upload", "ota-success", "users"].includes(activeTab);
  const tableHeaders = [
    "TIMESTAMP UTC",
    ...(!hideSerial ? ["SERIAL NO."] : []),
    "ACTOR",
    "ROLE",
    "EVENT TYPE",
    ...(!hideDeviceBaseline ? ["DEVICE / BASELINE"] : []),
    ...(!hideFwVersion ? ["FW VERSION"] : []),
  ];

  if (isInitialLoading) return <PageLoader />;

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Activity Trace</h1>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button type="button" variant="outline" size="sm" onClick={handleExport} disabled={exporting || loading}>
            <Download className="mr-1.5 h-4 w-4" />
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
          <span className="text-[11px] text-slate-400">Current tab and date range; the search box filters this page only.</span>
          {exportNote && (
            <span role={exportNote.ok ? "status" : "alert"} className={`text-xs ${exportNote.ok ? "text-emerald-700" : "text-red-600"}`}>
              {exportNote.text}
            </span>
          )}
        </div>
      </div>

      {/* Main card */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {/* Search + filter tabs */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-100">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08148C]/20 w-56"
              placeholder="Serial, actor, version..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          {/* Filter tabs */}
          <div className="flex items-center gap-2 flex-wrap">
            {FILTER_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => handleTab(t.key)}
                className={`text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${
                  activeTab === t.key
                    ? "bg-[#08148C] text-white border-[#08148C]"
                    : "bg-white text-slate-600 border-slate-300 hover:border-[#08148C] hover:text-[#08148C]"
                }`}
              >
                {t.label}
              </button>
            ))}
            <span className="ml-auto text-xs text-slate-400 whitespace-nowrap font-bold">
              {pagination.totalRecords.toLocaleString()} entries
              {hasFiltersApplied && <span className="ml-2 text-slate-500">(Filtered)</span>}
            </span>
            <div className="ml-8 flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600 whitespace-nowrap" htmlFor="audit-from-date">
                From
              </label>
              <input
                id="audit-from-date"
                type="date"
                className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#08148C]/20"
                value={fromDate}
                onChange={(e) => handleFromDate(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600 whitespace-nowrap" htmlFor="audit-to-date">
                To
              </label>
              <input
                id="audit-to-date"
                type="date"
                className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#08148C]/20"
                value={toDate}
                max={todayDate}
                onChange={(e) => handleToDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                {tableHeaders.map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading && (
                <tr>
                  <td colSpan={tableHeaders.length} className="py-12">
                    <PageLoader />
                  </td>
                </tr>
              )}
              {!loading && loadError && (
                <tr>
                  <td colSpan={tableHeaders.length} role="alert" className="text-center text-red-600 py-12">{loadError}</td>
                </tr>
              )}
              {!loading && !loadError && pageRecords.length === 0 && (
                <tr>
                  <td colSpan={tableHeaders.length} className="text-center text-slate-400 py-12">No records match.</td>
                </tr>
              )}
              {!loading && pageRecords.map((r) => {
                const { date, time } = formatTs(r.ts);
                return (
                  <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-2 text-slate-500 whitespace-nowrap">
                      <span className="block">{date}</span>
                      <span className="block text-xs">{time}</span>
                    </td>
                    {!hideSerial && <td className="px-5 py-4 font-mono text-xs text-slate-700 whitespace-nowrap">{r.serial}</td>}
                    <td className="px-5 py-4 font-bold text-slate-800 whitespace-nowrap">{r.actor}</td>
                    <td className="px-5 py-4 text-slate-400 text-xs whitespace-nowrap">{r.role}</td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-700">{r.eventType}</td>
                    {!hideDeviceBaseline && <td className="px-5 py-4 text-slate-600">{r.device}</td>}
                    {!hideFwVersion && <td className="px-5 py-4 font-mono text-[#08148C] font-semibold whitespace-nowrap">{r.fwVersion}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {records.length > 0 && (
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || pagination.page <= 1}
              onClick={() => fetchPage(pagination.page - 1)}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Prev
            </Button>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => {
              const current = pagination.page;
              const total = pagination.totalPages;
              const show = p === 1 || p === total || Math.abs(p - current) <= 1;
              const ellipsisBefore = p === current - 2 && current - 2 > 1;
              const ellipsisAfter = p === current + 2 && current + 2 < total;
              if (ellipsisBefore || ellipsisAfter) {
                return <span key={p} className="px-1 text-sm text-slate-400">...</span>;
              }
              if (!show) return null;
              return (
                <Button
                  key={p}
                  type="button"
                  variant={p === current ? "default" : "outline"}
                  size="sm"
                  disabled={loading}
                  onClick={() => fetchPage(p)}
                  className="min-w-9"
                >
                  {p}
                </Button>
              );
            })}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || pagination.page >= pagination.totalPages}
              onClick={() => fetchPage(pagination.page + 1)}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}